import { DocumentSignatureStatus, DocumentType } from '@bulthaup/api/db';
import { getSignerFullName } from '@bulthaup/api/entities/customer';
import { Badge, Button, Group, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconDownload,
  IconEye,
  IconFile,
  IconHistory,
  IconRefresh,
} from '@tabler/icons-react';

import { DocumentHistoryModal } from '../-document-history-modal';
import { SignatureRequestButton } from '../../../-components/-signature-request-button';
import { SignatureRequestStatusBadge } from '../../../-components/-signature-request-status-badge';
import { UploadQuoteButton } from '../../../-components/-upload-quote-button';
import { CancelSignatureRequestButton } from '../../../-components/cancel-signature-request-button';
import { RouterOutput, trpc } from '../../../../../../../services/trpc';
import { getUTDocumentUrl } from '../../../../../../../services/upload';
import { EditQuoteMetaSection } from './-edit-quote-meta-section';
import { extractLastPage } from './-extract-pdf-last-page';

export function QuoteSection({
  project,
}: {
  project: NonNullable<RouterOutput['projects']['get']>;
}) {
  const quoteDocument = project.documents.find(
    document => document.type === DocumentType.QUOTE,
  );

  const generateLetter = trpc.letters.generate.useMutation();

  const oldDocuments = project.documents
    .filter(document => document.type === DocumentType.QUOTE)
    .slice(1);

  if (!quoteDocument)
    return (
      <Stack>
        <UploadQuoteButton projectId={project.id} label="Déposer un marché" />
      </Stack>
    );

  if (!quoteDocument.signatureRequestId) {
    const signatureRecipient = getSignerFullName(project.customer);

    return (
      <Stack>
        <Text c="dimmed" size="sm" truncate style={{ flex: 1 }}>
          {quoteDocument.fileName}
        </Text>
        <Text c="dimmed" size="sm">
          {quoteDocument.hasReducedVatAttestation ? (
            <Badge variant="filled" color="blue">
              TVA 10%
            </Badge>
          ) : (
            <Badge variant="outline" color="gray">
              TVA classique
            </Badge>
          )}
        </Text>

        <Group align="center">
          <Button
            leftSection={<IconEye size={16} />}
            variant="outline"
            onClick={() => {
              window.open(getUTDocumentUrl(quoteDocument.utFileKey), '_blank');
            }}
          >
            Marché
          </Button>
          <UploadQuoteButton
            icon={<IconRefresh size={16} />}
            projectId={project.id}
            label="Remplacer le marché"
          />
        </Group>
        <Group>
          <Text c="dimmed" size="sm">
            Version : {quoteDocument.version}
          </Text>

          {oldDocuments.length > 0 && (
            <Button
              leftSection={<IconHistory size={16} />}
              variant="subtle"
              size="xs"
              onClick={() => {
                modals.open({
                  title: 'Historique des marchés',
                  size: 'lg',
                  children: <DocumentHistoryModal documents={oldDocuments} />,
                });
              }}
            >
              Anciennes versions
            </Button>
          )}
        </Group>

        <Group className="items-center">
          <EditQuoteMetaSection projectId={project.id} />
        </Group>
        <Group>
          <Button
            variant="outline"
            leftSection={<IconDownload size={16} />}
            loading={
              generateLetter.isPending &&
              generateLetter.variables?.letterType ===
                '8-demande-dacompte-35-avec-lettre-rib'
            }
            onClick={async () => {
              const letter = await generateLetter.mutateAsync({
                projectId: project.id,
                letterType: '8-demande-dacompte-35-avec-lettre-rib',
              });
              if (letter) {
                const blob = new Blob([new Uint8Array(letter)], {
                  type: 'application/pdf',
                });
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
              }
            }}
          >
            Voir lettre (Test)
          </Button>
        </Group>

        <Group>
          <SignatureRequestButton
            documentId={quoteDocument.id}
            recipient={signatureRecipient}
            label="Envoyer marché pour signature"
            signatureRequestName={`Marché ${project.name}`}
          />
        </Group>
      </Stack>
    );
  }

  return (
    <Stack>
      <Text c="dimmed" size="sm" truncate style={{ flex: 1 }}>
        {quoteDocument.fileName}
      </Text>
      <Group align="center">
        <Button
          leftSection={<IconEye size={16} />}
          variant="outline"
          onClick={() => {
            window.open(getUTDocumentUrl(quoteDocument.utFileKey), '_blank');
          }}
        >
          Marché
        </Button>
        <>
          {quoteDocument.signatureStatus === DocumentSignatureStatus.DONE &&
            quoteDocument.hasReducedVatAttestation && (
              <Button
                leftSection={<IconFile size={16} />}
                variant="outline"
                onClick={() => {
                  extractLastPage(getUTDocumentUrl(quoteDocument.utFileKey));
                }}
              >
                TVA 10%
              </Button>
            )}
          {quoteDocument.signatureStatus !==
            DocumentSignatureStatus.ONGOING && (
            <>
              <UploadQuoteButton
                icon={<IconRefresh size={16} />}
                projectId={project.id}
                label="Remplacer le marché signé"
              />
            </>
          )}
          {quoteDocument.signatureStatus ===
            DocumentSignatureStatus.ONGOING && (
            <CancelSignatureRequestButton
              documentId={quoteDocument.id}
              label="Annuler la signature"
            />
          )}
        </>
      </Group>

      <SignatureRequestStatusBadge status={quoteDocument.signatureStatus} />

      {oldDocuments.length > 0 && (
        <Group>
          <Button
            leftSection={<IconHistory size={16} />}
            variant="subtle"
            size="xs"
            onClick={() => {
              modals.open({
                title: 'Historique des marchés',
                size: 'md',
                children: <DocumentHistoryModal documents={oldDocuments} />,
              });
            }}
          >
            Anciennes versions
          </Button>
        </Group>
      )}
    </Stack>
  );
}
